import {HttpStatusCode} from '@angular/common/http';
import {AfterViewInit, Component, ViewChild} from '@angular/core';
import {AuthSessionApiService, FrontendConfig, SmsConfirmationStatus} from '@npck/auth-frontend-api';
import {OauthRedirectErrorCode} from '@npck/errors-api';
import {AnalyticsService} from '@npck/libs/analytics-sdk';
import {
    AppConfigService,
    BusinessErrorCode,
    extractRequestId,
    LocalizationService,
    normalizePhone,
    REQUEST_ID_HEADER,
    RouterService,
    rxSpinner,
    Spinner,
    toBusinessOrNull,
} from '@npck/libs/commons';
import {CodeInputComponent} from '@npck/libs/ui-kit';
import {SessionStorageService} from 'ngx-webstorage';
import {AuthHolderService, SMS_CODE_TIMER_START_TIME} from '../../domain/auth-holder.service';
import {OauthService} from '../../domain/oauth.service';
import {
    AUTH_SMS_CODE_FAILED,
    AUTH_SMS_CODE_REQUESTED,
    AUTH_SMS_CODE_SEND_AGAIN,
    AUTH_SMS_CODE_SUCCEED,
    SMS_PROVIDER_TIMEOUT,
} from '../../infra/analytics-events';
import {Path} from '../../path';

@Component({
    templateUrl: 'code.component.html',
    styleUrls: ['code.component.scss'],
    standalone: false
})
export class CodeComponent implements AfterViewInit {
    @ViewChild('codeInput') codeInput: CodeInputComponent;
    Spinner = Spinner;
    phone: string;
    codeLength = 4;
    disableCodeInput = false;
    confirmationStatus: SmsConfirmationStatus | BusinessErrorCode;
    smsCodeTimerStartTime: number;
    expirationTimeout = AppConfigService.config<FrontendConfig>().smsAttemptTimeoutSec;
    isTimerExpired = false;
    dedupeCode = false;
    previousCode: string;

    constructor(
        public oauthHolderService: AuthHolderService,
        public localizationService: LocalizationService,
        private routerService: RouterService,
        private authSessionApiService: AuthSessionApiService,
        private sessionStorageService: SessionStorageService,
        private analyticsService: AnalyticsService,
        private oauthService: OauthService,
    ) {
        this.phone = oauthHolderService.authSessionData.phone;
        const timerStartTime = this.sessionStorageService.retrieve(SMS_CODE_TIMER_START_TIME);
        this.startTimer(timerStartTime);
        if (this.isTimerExpired) {
            this.sendCode();
        }
    }

    ngAfterViewInit(): void {
        this.codeInput?.focusOnField(0);
    }

    sendCode(resend = false): void {
        // allow to enter the same code multiple times - needed on test envs where all codes are zeros
        this.dedupeCode = false;
        this.authSessionApiService.authSessionSendSms({phone: normalizePhone(this.phone)})
            .pipe(rxSpinner('code'))
            .subscribe(
                response => {
                    this.reset();
                    this.oauthHolderService.storeDeviceKey(response.deviceKey);
                    this.analyticsService.capture(
                        response.timeout
                            ? SMS_PROVIDER_TIMEOUT
                            : resend
                                ? AUTH_SMS_CODE_SEND_AGAIN
                                : AUTH_SMS_CODE_REQUESTED,
                    );
                    // regardless of the sms provider timeout, start the timer because the sms might've still been sent
                    this.startTimer();
                },
                error => {
                    const be = toBusinessOrNull(error);
                    if (be?.error == BusinessErrorCode.SMS_PER_SESSION_LIMIT_EXCEEDED) {
                        this.oauthService.handleOauthErrorAndRedirect(OauthRedirectErrorCode.sms_limit_exceeded, be.requestId);
                    } else if (be?.error == BusinessErrorCode.CONFIRMATION_CODE_RATE_LIMIT_EXCEEDED) {
                        this.disableCodeInput = true;
                        this.confirmationStatus = be.error;
                    } else if (error.status === HttpStatusCode.NotFound) {
                        this.oauthService.handleOauthErrorAndRedirect(OauthRedirectErrorCode.iin_not_found, extractRequestId(error));
                    } else {
                        throw error;
                    }
                });
    }

    onCodeComplete(code: string): void {
        if (code === this.previousCode && this.dedupeCode) {
            // code autofill on iOS sometimes triggers the input event twice, so we dedupe the code until it's sent
            return;
        }
        this.previousCode = code;
        this.dedupeCode = true;
        this.authSessionApiService.authSessionCheckSms({
            phone: normalizePhone(this.phone),
            code,
            deviceKey: this.oauthHolderService.deviceKey,
        }, 'response')
            .pipe(rxSpinner('code'))
            .subscribe(response => {
                const responseBody = response.body;
                switch (responseBody.status) {
                    case SmsConfirmationStatus.CONFIRMED:
                        this.analyticsService.capture(AUTH_SMS_CODE_SUCCEED, {reason: responseBody.status});
                        this.sessionStorageService.clear(SMS_CODE_TIMER_START_TIME);
                        if (this.oauthHolderService.authSessionData.hasOrganizationAuth) {
                            this.oauthHolderService.storeOrganizationToken(responseBody.token);
                            this.routerService.navigate(Path.BIN);
                        } else {
                            this.oauthHolderService.storeAuthSessionToken(responseBody.token);
                            this.routerService.navigate(Path.OAUTH_AUTHORIZE);
                        }
                        break;
                    case SmsConfirmationStatus.CODE_INVALID:
                        this.analyticsService.capture(AUTH_SMS_CODE_FAILED, {reason: responseBody.status});
                        this.confirmationStatus = responseBody.status;
                        break;
                    case SmsConfirmationStatus.CODE_EXPIRED:
                        this.analyticsService.capture(AUTH_SMS_CODE_FAILED, {reason: responseBody.status});
                        this.confirmationStatus = responseBody.status;
                        break;
                    case SmsConfirmationStatus.MAX_ATTEMPTS_EXCEEDED:
                        this.analyticsService.capture(AUTH_SMS_CODE_FAILED, {reason: responseBody.status});
                        this.sessionStorageService.clear(SMS_CODE_TIMER_START_TIME);
                        this.oauthService.handleOauthErrorAndRedirect(
                            OauthRedirectErrorCode.sms_code_attempts_exceeded, response.headers.get(REQUEST_ID_HEADER));
                        break;
                }
            });
    }

    onCodeChanged(): void {
        this.confirmationStatus = undefined;
    }

    onTimerExpired(): void {
        this.isTimerExpired = true;
    }

    private reset(): void {
        this.codeInput.reset(false);
        this.disableCodeInput = false;
        this.confirmationStatus = null;
    }

    private startTimer(timerStartTime = new Date().getTime()): void {
        this.smsCodeTimerStartTime = timerStartTime;
        this.sessionStorageService.store(SMS_CODE_TIMER_START_TIME, this.smsCodeTimerStartTime);
        const expirationTime = this.smsCodeTimerStartTime + (this.expirationTimeout * 1000);
        this.isTimerExpired = new Date().getTime() >= expirationTime;
    }
}
